import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lightbulb } from "lucide-react";
import { toast } from "sonner";

import CarregandoRelatorio from "../../relatorios-ofertas/comum/Carregando";

import BarrasPorDia from "../../ofertas-do-dia/admin/BarrasPorDia";
import { reais } from "../../ofertas-do-dia/admin/formato";
import { ROTA_HUB_RELATORIOS } from "../../ofertas-do-dia/admin/rotas";
import Numero from "../../recuperador/relatorio/Numero";
import { carregarRelatorio } from "./api";
import Combinacoes from "./Combinacoes";
import RankingSugestoes from "./RankingSugestoes";
import TabelaLugares from "./TabelaLugares";
import { CarregandoCards, CarregandoTabela } from "../../../components/carregando";
import { Skeleton } from "../../../components/ui/skeleton";

const PERIODOS = [7, 30, 90];
const hojeMenos = (dias) => { const d = new Date(); d.setDate(d.getDate() - dias); return d.toLocaleDateString("sv-SE"); };
const pct = (v) => `${String(v ?? 0).replace(".", ",")}%`;

// Relatórios → Relatório de Ofertas → Campanhas → Sugestões Inteligentes.
export default function RelatorioSugestoesPage() {
  const navigate = useNavigate();
  const [dias, setDias] = useState(30);
  const [rel, setRel] = useState(null);
  // Erro na 1ª leitura: aviso com "Tentar de novo" (antes piscava para sempre);
  // trocar o período mantém os dados na tela até os novos chegarem (25/10/2026).
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;
    setErro(false);
    carregarRelatorio({ inicio: hojeMenos(dias - 1), fim: hojeMenos(0) })
      .then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar o relatório"); } });
    return () => { vivo = false; };
  }, [dias, tentativa]);

  const r = rel?.resumo || {};
  const a = rel?.anterior || {};
  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-6" data-e2e="relatorio-sugestoes">
        <div>
          <button type="button" onClick={() => navigate(ROTA_HUB_RELATORIOS)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-4 h-4" /> Relatório de Campanhas
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-sky-500/10 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-sky-500" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Relatório das Sugestões Inteligentes</h1>
                {rel ? (
                  <p className="text-sm text-muted-foreground">{(rel.is_active ? "Sugestões ligadas no cardápio" : "Sugestões desligadas — histórico abaixo")}</p>
                ) : <Skeleton className="h-4 w-48 mt-1" />}
              </div>
            </div>
            <div className="flex rounded-lg border p-0.5 bg-card self-start sm:self-auto">
              {PERIODOS.map((p) => (
                <button key={p} type="button" onClick={() => setDias(p)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md ${dias === p ? "bg-sky-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  {p} dias
                </button>
              ))}
            </div>
          </div>
        </div>

        {!rel && erro ? <CarregandoRelatorio erro onTentar={() => setTentativa((t) => t + 1)} /> : !rel ? (
          // Carregamento com cards (25/10/2026): os 5 números, o ticket e a tabela piscando.
          <div className="space-y-6" aria-busy="true" aria-label="Carregando">
            <CarregandoCards quantos={5} className="grid grid-cols-2 lg:grid-cols-5 gap-3" />
            <CarregandoCards quantos={2} className="grid sm:grid-cols-2 gap-3" />
            <CarregandoTabela linhas={4} colunas={5} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <Numero rotulo="Exibidas" valor={r.exibidas} atual={r.exibidas} anterior={a.exibidas} e2e="num-exibidas" />
              <Numero rotulo="Adicionadas" valor={r.adicionadas} atual={r.adicionadas} anterior={a.adicionadas} e2e="num-adicionadas" />
              <Numero rotulo="Receita extra" valor={reais(r.receita)} atual={r.receita} anterior={a.receita} destaque e2e="num-receita-extra" />
              <Numero rotulo="Conversão" valor={pct(r.conversao_pct)} atual={r.conversao_pct} anterior={a.conversao_pct} e2e="num-conversao-sug" />
              <Numero rotulo="Taxa de adição" valor={pct(r.taxa_adicao_pct)} atual={r.taxa_adicao_pct} anterior={a.taxa_adicao_pct} e2e="num-adicao" />
            </div>
            <p className="text-[11px] text-muted-foreground -mt-3">
              Setas: comparação com os {dias} dias anteriores. Receita extra = só o valor dos itens que entraram pela sugestão e chegaram ao pedido (cancelado não conta). Conversão = itens vendidos ÷ vezes exibidas (produto vendido a peso conta 1 por venda).
            </p>
            <div className="grid sm:grid-cols-2 gap-3" data-e2e="impacto-ticket">
              <div className="rounded-xl border bg-card p-4">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Pedidos com sugestão</p>
                <p className="text-2xl font-black tabular-nums mt-0.5">{r.pedidos_com_sugestao}</p>
                <p className="text-xs text-muted-foreground">{pct(r.pedidos_pct)} dos pedidos do cardápio no período</p>
              </div>
              <div className="rounded-xl border bg-card p-4">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Ticket médio</p>
                <p className="text-2xl font-black tabular-nums mt-0.5">{r.pedidos_com_sugestao ? reais(r.ticket_com) : "—"} <span className="text-sm font-medium text-muted-foreground">com sugestão</span></p>
                <p className="text-xs text-muted-foreground">{reais(r.ticket_sem)} nos pedidos sem sugestão</p>
              </div>
            </div>
            <TabelaLugares lugares={rel.por_lugar} />
            <section className="rounded-xl border bg-card p-4 sm:p-5">
              <h3 className="font-semibold">Receita extra por dia</h3>
              <BarrasPorDia serie={rel.por_dia} valor={(d) => d.receita} rotulo="Receita extra por dia" />
            </section>
            <div className="grid lg:grid-cols-2 gap-4">
              <RankingSugestoes ranking={rel.ranking} />
              <Combinacoes combinacoes={rel.combinacoes} pedidos={rel.pedidos_analisados} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
